import type { AdminMoneyHealthHoldScanDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { isForbiddenError } from '../../../api';
import { scanText } from '../_lib/moneyHealthText';

/**
 * The two page-level banners.
 *
 * **Staleness — ONE banner, never four.** TanStack keeps the last successful
 * payload through a failed refetch, and the sections keep rendering it. A blip
 * that replaced four sections with four identical error alerts would delete
 * the only information on the screen. A 403 here is the mid-session revocation
 * case: the client's permission set is JWT-derived and up to 15 minutes stale,
 * so the gate can pass while the server refuses.
 *
 * **A capped scan is `info`, not an error.** Nothing is broken — the server
 * examined `scanCap` live holds and there are more, so both hold counts are
 * floors. Colouring that red would train people to ignore the colour.
 */
export const MoneyHealthStaleBanner = ({
  error,
  computedAtLabel,
}: {
  error: unknown;
  computedAtLabel: string;
}): ReactElement => {
  const { t } = useTranslation();
  const forbidden = isForbiddenError(error);
  return (
    <Alert severity={forbidden ? 'error' : 'warning'} data-testid="money-health-stale">
      <AlertTitle>
        {forbidden
          ? t('moneyHealth.forbidden.title')
          : t('moneyHealth.stale.title', { at: computedAtLabel })}
      </AlertTitle>
      {forbidden ? t('moneyHealth.forbidden.body') : t('moneyHealth.stale.body')}
    </Alert>
  );
};

export const MoneyHealthScanCappedBanner = ({
  scan,
}: {
  scan: AdminMoneyHealthHoldScanDto;
}): ReactElement => {
  const { t } = useTranslation();
  const text = scanText(scan);
  return (
    <Alert severity="info" data-testid="money-health-scan-capped">
      {t(text.key, { ...text.values })}
    </Alert>
  );
};
